import { useEffect, useRef } from 'react'
import { ChevronRight, Heart, Minimize2 } from 'lucide-react'
import { Link } from 'react-router-dom'
import { FavoriteHeartButton } from '@/components/favorites/favorite-heart-button'
import { canonicalProjectUrl } from '@/lib/canonical-url'
import type { BrowseProject } from '@/api/types'

interface BrowseImageCardProps {
  project: BrowseProject
  isAuthenticated: boolean
  isFavoritedByMe: boolean
  isExpanded: boolean
  /** Toggle the expanded state for this card. Cards expand independently of each other. */
  onToggleExpanded: (projectId: string) => void
}

/**
 * Image-fills-the-card primitive used by Card mode (uniform grid) and Inspiration mode
 * (variable-size bento). Two interaction states:
 *
 * <ul>
 *   <li><b>Default</b> — just the photo (or a hash-coloured gradient if no hero). Title +
 *       author handle + heart count fade in along a bottom gradient on hover.
 *   <li><b>Expanded</b> — clicking the card grows it: bottom gradient becomes permanent and
 *       richer (title + description excerpt + author + heart count + an explicit "Show more"
 *       button + a collapse button). Cards expand independently — clicking a second card
 *       leaves the first expanded. Click the collapse icon (top-left) to collapse one;
 *       clicking anywhere on the photo of an expanded card also collapses it. "Show more" is
 *       the only action that actually navigates.
 * </ul>
 *
 * <p>The parent grid is responsible for upsizing the *cell* when this card is expanded — the
 * card itself only changes its inner overlay state.
 */
export const BrowseImageCard = ({
  project,
  isAuthenticated,
  isFavoritedByMe,
  isExpanded,
  onToggleExpanded,
}: BrowseImageCardProps) => {
  const background: React.CSSProperties = project.heroThumbUrl
    ? {
        backgroundImage: `url(${project.heroThumbUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    : { backgroundColor: gradientColor(project.id) }

  // Scroll the card into view when it becomes expanded so the preview is never clipped at
  // the bottom of the viewport. `block: 'nearest'` is gentle — only scrolls if the card is
  // actually off-screen, no jumpy behavior on already-visible cards.
  const articleRef = useRef<HTMLElement | null>(null)
  useEffect(() => {
    if (isExpanded && articleRef.current) {
      articleRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [isExpanded])

  return (
    <article
      ref={articleRef}
      className={
        'group relative h-full w-full overflow-hidden rounded-md bg-muted transition-shadow ' +
        (isExpanded ? 'ring-2 ring-primary shadow-xl' : '')
      }
      style={background}
    >
      {/* Card-wide tap target — toggles expanded state for this card. Cards expand
          independently of each other. Show more + collapse icon each have their own handlers
          above this layer. */}
      <button
        type="button"
        onClick={() => onToggleExpanded(project.id)}
        aria-label={
          isExpanded ? `Collapse preview of ${project.title}` : `Preview ${project.title}`
        }
        className="absolute inset-0 z-0 cursor-pointer"
      />

      {/* Heart pinned top-right when signed in. Always visible. */}
      {isAuthenticated && (
        <div className="absolute right-2 top-2 z-20">
          <FavoriteHeartButton
            projectId={project.id}
            isFavoritedByMe={isFavoritedByMe}
            variant="overlay"
          />
        </div>
      )}

      {/* Expanded-state collapse icon, top-left so it doesn't collide with the heart. */}
      {isExpanded && (
        <button
          type="button"
          onClick={() => onToggleExpanded(project.id)}
          aria-label="Collapse preview"
          className="absolute left-2 top-2 z-20 inline-flex h-7 w-7 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur transition-colors hover:bg-black/80"
        >
          <Minimize2 className="h-3.5 w-3.5" />
        </button>
      )}

      {/* Info overlay: bottom gradient. On hover (default state) the gradient + minimal info
          fade in. When expanded, the gradient is permanent and the info is richer — title +
          description excerpt + author + a "Show more" Link button. */}
      <div
        className={
          'pointer-events-none absolute inset-x-0 bottom-0 z-10 ' +
          'bg-gradient-to-t from-black/90 via-black/70 to-transparent ' +
          'px-3 pb-3 pt-10 transition-opacity duration-200 ' +
          (isExpanded
            ? 'opacity-100'
            : 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100')
        }
      >
        <h3
          className={
            'font-semibold leading-snug text-white ' +
            (isExpanded ? 'text-base' : 'line-clamp-2 text-sm')
          }
        >
          {project.title}
        </h3>
        {isExpanded && project.descriptionExcerpt && (
          <p className="mt-1 line-clamp-4 text-xs text-white/80">{project.descriptionExcerpt}</p>
        )}
        <div className="mt-2 flex items-center gap-2 text-xs text-white/85">
          <span>@{project.author.handle}</span>
          {project.favoriteCount > 0 && (
            <span className="inline-flex items-center gap-0.5">
              <Heart className="h-3 w-3" aria-hidden />
              {project.favoriteCount}
            </span>
          )}
        </div>
        {isExpanded && (
          <div className="pointer-events-auto mt-3">
            <Link
              to={canonicalProjectUrl(project.author.handle, project.slug)}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
            >
              Show more
              <ChevronRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        )}
      </div>
    </article>
  )
}

/** Same hash-to-HSL as `ProjectHeroBackground.fallbackColor` for visual consistency. */
const gradientColor = (seed: string): string => {
  let hash = 0
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0
  }
  const hue = Math.abs(hash) % 360
  return `hsl(${hue}, 45%, 70%)`
}
